import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, ChevronDown, ChevronUp, Sparkles, MessageSquarePlus, X, Loader2, Send } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_STEPS = [
  { key: 'verify', label: 'Verify your account', done: false },
  { key: 'create_form', label: 'Create your first form', done: false },
  { key: 'add_element', label: 'Add your first element', done: false },
  { key: 'publish_form', label: 'Publish your first form', done: false },
  { key: 'first_response', label: 'Get your first submission', done: false },
];

const EXPANDED_KEY = 'ff_onboarding_expanded';
// Sticky once-true flag — the user has completed all five onboarding
// goals at least once, so we permanently swap the checklist for the
// "How can we improve?" feedback pill. Storing it in localStorage means
// transient progress drops (e.g. a deleted form) won't flip the user
// back to the checklist view.
const COMPLETED_KEY = 'ff_onboarding_completed';

const OnboardingChecklist = () => {
  const [steps, setSteps] = useState(DEFAULT_STEPS);
  const [completed, setCompleted] = useState(0);
  const [total, setTotal] = useState(5);
  const [expanded, setExpanded] = useState(() => localStorage.getItem(EXPANDED_KEY) !== 'false');
  const [loading, setLoading] = useState(true);
  // Sticky completion flag — once set to true, stays true forever for
  // this browser (localStorage persists across sessions / logins).
  const [onboardingDone, setOnboardingDone] = useState(
    () => localStorage.getItem(COMPLETED_KEY) === 'true'
  );
  // Feedback dialog state.
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [wantsFollowup, setWantsFollowup] = useState(false);
  const [sending, setSending] = useState(false);
  const navigate = useNavigate();

  const toggleExpanded = () => {
    const next = !expanded;
    setExpanded(next);
    localStorage.setItem(EXPANDED_KEY, String(next));
  };

  useEffect(() => {
    fetchProgress();
  }, []);

  const fetchProgress = async () => {
    try {
      const token = localStorage.getItem('ff_token');
      const res = await axios.get(`${API}/auth/onboarding-progress`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setSteps(res.data.steps);
      setCompleted(res.data.completed);
      setTotal(res.data.total);
      // Latch the "done" flag the first time we observe all goals
      // complete. Subsequent fetches can't undo it.
      if (res.data.completed >= res.data.total && res.data.total > 0) {
        localStorage.setItem(COMPLETED_KEY, 'true');
        setOnboardingDone(true);
      }
    } catch {
      setSteps(DEFAULT_STEPS);
    } finally {
      setLoading(false);
    }
  };

  const handleStepClick = (step) => {
    if (step.done) return;
    switch (step.key) {
      case 'verify':
        navigate('/settings');
        break;
      default:
        break;
    }
  };

  const submitFeedback = async () => {
    const trimmed = message.trim();
    if (!trimmed) {
      toast.error('Tell us what you have in mind first.');
      return;
    }
    setSending(true);
    try {
      const token = localStorage.getItem('ff_token');
      await axios.post(
        `${API}/feedback`,
        { message: trimmed, wants_followup: wantsFollowup },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      toast.success(
        wantsFollowup
          ? "Thanks! We'll be in touch."
          : 'Thanks for the feedback — every note gets read.'
      );
      setMessage('');
      setWantsFollowup(false);
      setFeedbackOpen(false);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
          'Could not send feedback. Please try again.'
      );
    } finally {
      setSending(false);
    }
  };

  if (loading) return null;

  // ────────────────────────────────────────────────────────────────
  // POST-ONBOARDING UI: floating "How can we improve?" pill.
  // Shown once the user has hit 5/5 at least once (sticky via
  // localStorage). Clicking opens an inline feedback composer.
  // ────────────────────────────────────────────────────────────────
  if (onboardingDone) {
    return (
      <div className="fixed bottom-5 right-5 z-40" data-testid="feedback-pill-root">
        {!feedbackOpen ? (
          <button
            type="button"
            onClick={() => setFeedbackOpen(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-zinc-900 text-white rounded-full shadow-lg hover:bg-zinc-800 transition-all hover:shadow-xl active:scale-95"
            data-testid="feedback-pill-button"
          >
            <MessageSquarePlus className="w-4 h-4" />
            <span className="text-sm font-medium">How can we improve?</span>
          </button>
        ) : (
          <div
            className="w-80 bg-white border border-zinc-200 rounded-xl shadow-2xl overflow-hidden"
            data-testid="feedback-composer"
          >
            <div className="flex items-center justify-between px-4 py-3 bg-zinc-900 text-white">
              <div className="flex items-center gap-2">
                <MessageSquarePlus className="w-4 h-4" />
                <span className="text-sm font-semibold">How can we improve?</span>
              </div>
              <button
                type="button"
                onClick={() => setFeedbackOpen(false)}
                className="text-zinc-400 hover:text-white transition-colors"
                aria-label="Close feedback composer"
                data-testid="feedback-close-button"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Share an idea, a missing feature, or what's been frustrating you…"
                rows={5}
                maxLength={5000}
                className="w-full text-sm text-zinc-900 placeholder-zinc-400 border border-zinc-200 rounded-lg p-3 resize-none focus:outline-none focus:border-zinc-900 focus:ring-1 focus:ring-zinc-900"
                data-testid="feedback-textarea"
                autoFocus
              />
              <label className="flex items-start gap-2 text-xs text-zinc-600 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={wantsFollowup}
                  onChange={(e) => setWantsFollowup(e.target.checked)}
                  className="mt-0.5 accent-zinc-900"
                  data-testid="feedback-followup-checkbox"
                />
                <span>
                  Receive a follow-up email with an update on this request
                </span>
              </label>
              <button
                type="button"
                onClick={submitFeedback}
                disabled={sending || !message.trim()}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-zinc-900 text-white text-sm font-medium rounded-lg hover:bg-zinc-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                data-testid="feedback-submit-button"
              >
                {sending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sending…
                  </>
                ) : (
                  <>
                    <Send className="w-3.5 h-3.5" />
                    Send feedback
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  const pct = Math.round((completed / total) * 100);

  return (
    <div className="fixed bottom-5 right-5 z-40 w-80" data-testid="onboarding-checklist">
      <div
        onClick={toggleExpanded}
        className="flex items-center justify-between px-4 py-3 bg-zinc-900 text-white cursor-pointer select-none"
        style={{ borderRadius: expanded ? '12px 12px 0 0' : '12px' }}
        data-testid="onboarding-header"
      >
        <div className="flex items-center gap-2.5">
          <Sparkles className="w-4 h-4 text-white" />
          <span className="text-sm font-semibold tracking-tight">Getting Started</span>
          <span className="text-xs text-zinc-400 font-medium">{completed}/{total}</span>
        </div>
        {expanded ? <ChevronDown className="w-4 h-4 text-zinc-400" /> : <ChevronUp className="w-4 h-4 text-zinc-400" />}
      </div>

      {expanded && (
        <div className="bg-white border border-t-0 border-zinc-200 rounded-b-xl shadow-xl overflow-hidden" data-testid="onboarding-body">
          <div className="px-4 pt-3 pb-2">
            <div className="w-full h-1.5 bg-zinc-100 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{ width: `${pct}%`, backgroundColor: '#FE6A6B' }}
                data-testid="onboarding-progress-bar"
              />
            </div>
          </div>

          <ul className="px-2 pb-3">
            {steps.map((step) => (
              <li
                key={step.key}
                onClick={() => handleStepClick(step)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                  step.done ? 'opacity-60' : 'hover:bg-zinc-50 cursor-pointer'
                }`}
                data-testid={`onboarding-step-${step.key}`}
              >
                <div
                  className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center border-2 transition-all ${
                    step.done ? 'border-transparent' : 'border-zinc-300'
                  }`}
                  style={step.done ? { backgroundColor: '#FE6A6B', borderColor: '#FE6A6B' } : {}}
                >
                  {step.done && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                </div>
                <span className={`text-sm ${step.done ? 'line-through text-zinc-400' : 'text-zinc-700 font-medium'}`}>
                  {step.label}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default OnboardingChecklist;
